import { useState, useEffect, useRef } from "react";

/** Delay before showing skeleton (ms). Prevents flash on fast loads. */
export const SKELETON_DELAY = 200;
/** Minimum time skeleton stays visible once shown (ms). Prevents skeleton flicker. */
export const SKELETON_MIN_DISPLAY = 400;

/**
 * Wraps a loading boolean to prevent skeleton flash.
 * - If loading resolves within SKELETON_DELAY ms, skeleton is never shown.
 * - Once skeleton IS shown, it stays for at least SKELETON_MIN_DISPLAY ms.
 */
export function useDelayedLoading(isLoading: boolean): boolean {
  const [showSkeleton, setShowSkeleton] = useState(false);
  const shownAtRef = useRef<number | null>(null);
  const delayTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const minDisplayTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (isLoading) {
      delayTimerRef.current = setTimeout(() => {
        shownAtRef.current = Date.now();
        setShowSkeleton(true);
      }, SKELETON_DELAY);
    } else {
      clearTimeout(delayTimerRef.current);

      if (shownAtRef.current !== null) {
        const elapsed = Date.now() - shownAtRef.current;
        const remaining = SKELETON_MIN_DISPLAY - elapsed;

        if (remaining > 0) {
          minDisplayTimerRef.current = setTimeout(() => {
            setShowSkeleton(false);
            shownAtRef.current = null;
          }, remaining);
        } else {
          setShowSkeleton(false);
          shownAtRef.current = null;
        }
      } else {
        setShowSkeleton(false);
      }
    }

    return () => {
      clearTimeout(delayTimerRef.current);
      clearTimeout(minDisplayTimerRef.current);
    };
  }, [isLoading]);

  return showSkeleton;
}
